import { useCallback, useEffect, useMemo, useRef } from 'react'

import { EDITOR_DRAG_DETECTION_THRESHOLD } from '@editor/config/constants'
import { PointArray } from '@editor/interfaces/attribute'
import { LineStyle } from '@editor/interfaces/canvas'
import { RectangleMeshProps } from '@editor/interfaces/canvas/rectangleMesh'
import { LINE_WIDTH_TO_GAP_RATIO } from '@editor/pages/projects/blueprint-editor/constants'
import useProjectionPlane from '@editor/pages/projects/blueprint-editor/hooks/useProjectionPlane'
import { RootState, useAppDispatch } from '@editor/store/app'
import {
  resetEditingElementId,
  resetWorkingElementId,
  setEditingElementId,
  setHoveredElementId,
  toggleSelectedElementIds,
} from '@editor/store/tool'
import { useFrame, useThree } from '@react-three/fiber'
import { useSelector } from 'react-redux'
import { MeshStandardMaterial, ShapeUtils, Vector2, Vector3 } from 'three'
import { useThrottledCallback } from 'use-debounce'

import useObjectsManagement from './useObjectsManagement'
import useParams from './useParams'

const useAdjustmentHandlers = (
  { id, positions, outline, visible, selectable, isEditing, isWorking, onUpdated }: RectangleMeshProps,
  { rectangleGeometry, outlineObject, meshRef, setAnchorPositions }: ReturnType<typeof useObjectsManagement>,
  { matrix, zAlignMatrix }: ReturnType<typeof useParams>,
) => {
  // Hooks
  const { camera, gl } = useThree()
  const { worldToScreen, screenToWorld } = useProjectionPlane(camera, gl)

  // Store
  const dispatch = useAppDispatch()
  const currentFillColor = useSelector((state: RootState) => state.toolUtil.core.currentFillColor)
  const currentStrokeColor = useSelector((state: RootState) => state.toolUtil.core.currentStrokeColor)
  const currentAnchorPosition = useSelector((state: RootState) => state.toolUtil.core.currentAnchorPosition)
  const selectedElementIds = useSelector((state: RootState) => state.toolUtil.core.selectedElementIds)

  // Refs
  const movingStartLocalPointRef = useRef<Vector2 | null>(null) // screen
  const movingStartWorldPointRef = useRef<Vector3 | null>(null) // world
  const isDraggingRef = useRef(false)
  const isAdjustingAnchorRef = useRef<number | null>(isWorking ? 2 : null)
  const dragDistanceRef = useRef(0)

  const tempPositionsRef = useRef<Vector3[]>([])
  const tempV2Ref = useRef(new Vector2())

  // Props
  const isAdjusting = useMemo(() => isEditing || isWorking, [isEditing, isWorking])
  const isSelected = selectedElementIds.includes(id)

  /**
   * Generate 4 corner points for the rectangle
   */
  const getRectanglePoints = useCallback(
    (currentPos: PointArray, anchorIndex?: number) => {
      if (isAdjusting && anchorIndex !== undefined) {
        const origin = new Vector3().setFromMatrixPosition(matrix)
        const adjusted = new Vector3(...currentPos).sub(origin)
        const fixedIndex = (anchorIndex + 2) % 4

        const invert = zAlignMatrix.clone().invert()

        if (positions) {
          const fixed = new Vector3(...positions[fixedIndex]).sub(origin)

          // project adjusted + fixed to 2D
          const adjusted2d = worldToScreen(adjusted)
          const fixed2d = worldToScreen(fixed)

          if (!adjusted2d || !fixed2d) return null

          // derive perpendiculars in 2D
          const perp1 = screenToWorld(new Vector2(adjusted2d.x, fixed2d.y))
          const perp2 = screenToWorld(new Vector2(fixed2d.x, adjusted2d.y))

          if (!perp1 || !perp2) return null

          const newPositions = [...positions]
          newPositions[(anchorIndex + 3) % 4] = (anchorIndex % 2 === 0 ? perp1 : perp2) // despite being +3, we're targetting the previous anchor
            .add(origin)
            .setX((anchorIndex % 2 === 0 ? perp1 : perp2).x)
            .toArray()
          newPositions[anchorIndex] = currentPos
          newPositions[(anchorIndex + 1) % 4] = (anchorIndex % 2 === 0 ? perp2 : perp1)
            .add(origin)
            .setX((anchorIndex % 2 === 0 ? perp2 : perp1).x)
            .toArray()

          // get origin's z in local space and apply to all points.
          // this is to ensure the rectangle remains flat on the projection plane.
          const originLocal = origin.clone().applyMatrix4(zAlignMatrix)
          return newPositions.map((point) =>
            new Vector3(...point).applyMatrix4(zAlignMatrix).setZ(originLocal.z).applyMatrix4(invert),
          )
        }

        // fallback path (no positions)
        // FIXME: vertices-based rect, does not work. it's the same as the original.
        const corner1 = new Vector3()
        const corner3 = new Vector3(...currentPos).sub(origin)

        const corner3z = corner3.clone().applyMatrix4(zAlignMatrix)
        const corner2 = new Vector3(corner1.x, corner3z.y, 0)
        const corner4 = new Vector3(-corner3z.x, corner1.y, 0)

        return [corner1, corner2, corner3.setX(-corner3.x), corner4].map((c) => c.applyMatrix4(invert))
      }

      if (positions && positions.length >= 4) {
        return positions.map((point) => new Vector3(...point))
      }

      return null
    },
    [isAdjusting, matrix, positions, zAlignMatrix, screenToWorld, worldToScreen],
  )

  const applyGeometryChanges = useCallback(
    (rectanglePoints: Vector3[]) => {
      if (!rectangleGeometry) return

      // --- Update rectangle geometry
      rectangleGeometry.setFromPoints(rectanglePoints)
      rectangleGeometry.computeBoundingBox()
      rectangleGeometry.computeBoundingSphere()

      const indi = ShapeUtils.triangulateShape(
        rectanglePoints.map((p) => p.clone().project(camera)),
        [],
      )
      rectangleGeometry.setIndex(indi.flat())
      rectangleGeometry.computeVertexNormals()

      // --- Update outline
      if (!outlineObject) return

      const closedPath = [...rectanglePoints, rectanglePoints[0]]
      const points = closedPath.flatMap((p) => p.toArray())

      // Update geometry positions
      outlineObject.geometry.setPositions(points)
      outlineObject.geometry.computeVertexNormals()

      // -- Update anchors
      setAnchorPositions((prev) => prev.map((anchor, index) => ({ ...anchor, position: rectanglePoints[index] })))
    },
    [outlineObject, rectangleGeometry, camera, setAnchorPositions],
  )

  const handleDragAdjustment = useCallback(
    (curPos: PointArray) => {
      if (!rectangleGeometry || isAdjustingAnchorRef.current !== null || !isEditing || !positions) return

      if (!movingStartWorldPointRef.current) {
        movingStartWorldPointRef.current = new Vector3(...curPos)
        return
      }

      const current = new Vector3(...curPos)
      const translation = new Vector3().subVectors(current, movingStartWorldPointRef.current)
      tempPositionsRef.current = positions.map((p) => new Vector3(...p).add(translation))
      dragDistanceRef.current = translation.length()

      applyGeometryChanges(tempPositionsRef.current)
    },
    [applyGeometryChanges, isAdjustingAnchorRef, isEditing, positions, rectangleGeometry],
  )

  const handleAnchorAdjustment = useCallback(
    (curPos: PointArray) => {
      if (isAdjustingAnchorRef.current === null) return

      tempPositionsRef.current = getRectanglePoints(curPos, isAdjustingAnchorRef.current) || []
      if (!tempPositionsRef.current) return

      applyGeometryChanges(tempPositionsRef.current)
    },
    [applyGeometryChanges, getRectanglePoints],
  )

  const handleStylesAdjustment = useCallback(
    (currentFill?: string, curentStroke?: string) => {
      if (!outlineObject || !outline?.color) return

      // -- Outline styles
      outlineObject.material.dashed = outline.style === LineStyle.Dashed
      outlineObject.material.color.set((isSelected || isWorking) && curentStroke ? curentStroke : outline?.color)
      outlineObject.material.linewidth = outline.thickness || 0
      outlineObject.material.dashSize = outlineObject.material.linewidth * LINE_WIDTH_TO_GAP_RATIO
      outlineObject.material.gapSize = outlineObject.material.linewidth * LINE_WIDTH_TO_GAP_RATIO
      outlineObject.computeLineDistances()

      // -- Plane styles
      if (
        !meshRef.current ||
        !(meshRef.current.material instanceof MeshStandardMaterial) ||
        !currentFill ||
        (!isSelected && !isWorking)
      )
        return
      meshRef.current.visible = true // mesh is invisible by default due to no color.
      meshRef.current.material.color.set(currentFill)
    },
    [meshRef, outlineObject, outline, isWorking, isSelected],
  )

  useEffect(() => {
    const handleMouseUp = (ev: MouseEvent) => {
      movingStartLocalPointRef.current = null

      const inCanvas = document.getElementById('canvasBox')?.contains(ev.target as HTMLElement)
      if (!selectable || (!isDraggingRef.current && isAdjustingAnchorRef.current === null) || !inCanvas) return

      ev.stopPropagation()

      dispatch(resetEditingElementId(id))
      dispatch(resetWorkingElementId(id))
      dispatch(toggleSelectedElementIds({ ids: [id], selected: true }))
      isAdjustingAnchorRef.current = null
      movingStartWorldPointRef.current = null

      // Only trigger update if there was a change
      if (isDraggingRef.current && dragDistanceRef.current === 0) {
        isDraggingRef.current = false
        dragDistanceRef.current = 0
        return
      }

      onUpdated?.(tempPositionsRef.current.map((v) => v.toArray() as PointArray))
      tempPositionsRef.current = []
      isDraggingRef.current = false
      dragDistanceRef.current = 0
    }

    const handleMouseMove = (ev: MouseEvent) => {
      if (movingStartLocalPointRef.current) {
        const distance = movingStartLocalPointRef.current.distanceTo(tempV2Ref.current.set(ev.clientX, ev.clientY))
        if (distance > EDITOR_DRAG_DETECTION_THRESHOLD && !isDraggingRef.current) {
          isDraggingRef.current = true
          dispatch(setEditingElementId(id))
          dispatch(setHoveredElementId(''))
        }
      }
    }

    window.addEventListener('mouseup', handleMouseUp)
    window.addEventListener('mousemove', handleMouseMove)
    return () => {
      window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [isDraggingRef, id, selectable, onUpdated, dispatch])

  /**
   * Update visual aspect that needs to be adjusted on every frame.'
   * Wrap in useThrottledCallback to avoid excessive calculations.
   */
  const frameHandler = useThrottledCallback(() => {
    if (!visible) return

    // -- Style adjustment always runs
    handleStylesAdjustment(currentFillColor, currentStrokeColor)

    // Need permission to edit
    if (!selectable) return

    // -- Geometry adjustment handlers
    if (!currentAnchorPosition) return

    // geometries update as needed
    if (isDraggingRef.current) handleDragAdjustment(currentAnchorPosition)
    else if (isAdjustingAnchorRef.current !== null) handleAnchorAdjustment(currentAnchorPosition)
  }, 1000 / 60)
  useFrame(frameHandler)

  return {
    isDraggingRef,
    isAdjustingAnchorRef,

    movingStartLocalPointRef,
    movingStartWorldPointRef,
  }
}

export default useAdjustmentHandlers
